import { useEffect } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { useDebounce } from 'react-use';
import dayjs from 'dayjs';

// Atoms
import {
    DatepickerAtomType,
    datepickerAtom
} from '@/components/base/Datepicker/DatepickerComponent/Atoms/datepickerAtom';
import { updatePeriodAtom } from '@/components/base/Datepicker/DatepickerComponent/Atoms/periodsAtom';

// Utils
import { checkDateWithMinMax } from '@/utils/datepicker/periods';

type AtomsListenerProps = Pick<DatepickerAtomType, 'settings'> & {
    children: React.ReactNode;
};

export function AtomsListener({ settings, children }: AtomsListenerProps) {
    const [datepicker, setDatepicker] = useAtom(datepickerAtom);

    const updatePeriod = useSetAtom(updatePeriodAtom);

    const datepickerPeriod = datepicker.mode === 'range' ? datepicker.period : null;
    const dateStringified = JSON.stringify(datepicker.date);

    const minDate = datepicker.settings?.minDate;
    const maxDate = datepicker.settings?.maxDate;

    /**
     * Trigger onChange each time the date range is updated
     */
    useDebounce(
        () => {
            if (typeof datepicker?.onChange === 'function') {
                if (datepicker.mode === 'range' && datepicker.date.to) {
                    const { from, to } = datepicker.date;
                    datepicker.onChange({ date: { from, to }, period: datepicker.period });
                }

                if (datepicker.mode === 'single') {
                    datepicker.onChange({ date: datepicker.date });
                }
            }
        },
        100,
        [dateStringified]
    );

    useEffect(() => {
        /**
         * Check if the date is within the minDate and maxDate range
         */
        if (datepicker.mode === 'single') {
            setDatepicker((draft) => {
                if (draft.mode === 'single') {
                    const newDate = checkDateWithMinMax({
                        currentDate: dayjs(draft.date),
                        minDate,
                        maxDate
                    });

                    draft.date = newDate.toDate();
                }
            });
            return;
        }

        if (datepicker.mode === 'range' && datepickerPeriod) {
            // Restart the "updatePeriod" to keep the dates up to date
            return updatePeriod(datepickerPeriod);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [datepickerPeriod, dateStringified, minDate, maxDate]);

    useEffect(() => {
        // Update settings
        setDatepicker((draft) => {
            draft.settings = settings;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(settings)]);

    return children;
}
